import {logger} from "./logger";

const log = logger.sub("SoundMachine");

export class SoundMachine {

    private currentAudioSourceNode?: AudioBufferSourceNode;

    constructor() {
        try {
            this.audioContext = new AudioContext();
        } catch (e) {
            log.error("Cannot load WebAudio AudioContext");
        }
    }

    readonly audioContext?: AudioContext;

    speak(text: string, language: string) {
        return new Promise((resolve, reject) => {
            const voice = findVoiceForLanguage(language);
            if (voice) {
                // prepare message
                log.info(`Speaking: "${text}" in ${language}`);
                const message = new SpeechSynthesisUtterance(text);
                message.text = text;
                message.voice = voice;
                message.rate = 0.9;

                // resolve promise when speaking ends
                message.addEventListener("end", resolve);
                message.addEventListener("error", () => reject(new Error("error while speaking")));
                // reject promise after timeout
                window.setTimeout(() => reject(new Error("timeout")), 5000);
                // finally speak
                speechSynthesis.speak(message);
            }
            else {
                log.error("cannot speak, got no voice");
                reject("cannot speak, got no voice");
            }
        });
    }

    async play(url: string) {
        if (this.audioContext) {
            if (this.currentAudioSourceNode) {
                this.currentAudioSourceNode.stop(0);
                this.currentAudioSourceNode = undefined;
            }
            const audioData = await this.loadAudioFromUrl(url);
            await this.audioContext.resume();
            this.currentAudioSourceNode = this.audioContext.createBufferSource();
            this.currentAudioSourceNode.buffer = audioData;
            this.currentAudioSourceNode.connect(this.audioContext.destination);
            this.currentAudioSourceNode.start(0);
        }
    }

    async sound(url: string) {
        if (this.audioContext) {
            const audioData = await this.loadAudioFromUrl(url);
            await this.audioContext.resume();
            const bufferSource = this.audioContext.createBufferSource();
            bufferSource.buffer = audioData;
            bufferSource.connect(this.audioContext.destination);
            bufferSource.start(0);
        }
    }

    private cache = new Map<string, AudioBuffer>();

    private async loadAudioFromUrl(url: string) {
        let result = this.cache.get(url);
        if (!result) {
            log.verbose("sound not found in cache", url);
            const response = await window.fetch(url);
            const data = await response.arrayBuffer();
            result = await this.decodeAudioData(data);
            this.cache.set(url, result);
            log.verbose("sound loaded", url);
        }
        return result;
    }

    async decodeAudioData(data: ArrayBuffer) {
        return new Promise<AudioBuffer>((resolve, reject) => {
            if (this.audioContext) {
                this.audioContext.decodeAudioData(data, buffer => resolve(buffer), reject);
            }
            else {
                log.error("cannot decodeAudioData, got no AudioContext");
                reject("cannot decodeAudioData, got no AudioContext");
            }
        });
    }
}

export const soundMachine = new SoundMachine();

function findVoiceForLanguage(language: string) {
    log.info("voices", speechSynthesis.getVoices());

    const candidates = speechSynthesis.getVoices()
        .filter(voice => voice.lang.toLowerCase().indexOf(language.toLowerCase()) == 0)
        .filter(voice => voice.localService);

    log.info(`Found ${candidates.length} voices for language ${language}`, candidates);

    const defaultVoice = candidates.filter(voice => voice.default).pop();
    if (defaultVoice) {
        log.info("Using default voice", defaultVoice);
        return defaultVoice;
    }

    const preferredVoice = candidates.find(voice => PREFERRED_VOICES.includes(voice.name));
    if (preferredVoice) {
        log.info("Using preferred voice", preferredVoice);
        return preferredVoice;
    }

    log.info(`No default or preferred voice found for language ${language}, using first candidate`);
    return candidates.shift();
}

// Preferred voices in the espeak-ng set of voices
const PREFERRED_VOICES = [
    "English (America)+Half-LifeAnnouncementSystem espeak-ng",
    "German+Half-LifeAnnouncementSystem espeak-ng",
]

